import React from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ShieldCheck, Handshake, ArrowRight } from "lucide-react";
import { SiteFooter } from "@/components/SiteFooter";
import { PublicHeader } from "@/components/PublicHeader";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";

const STEPS = [
  { icon: Building2, title: "Submit your property", desc: "Share the details and photos of the home you want to sell." },
  { icon: ShieldCheck, title: "We review it", desc: "PropertyConnect reviews your submission for quality and completeness." },
  { icon: Handshake, title: "Get represented", desc: "A vetted professional is assigned to represent and market your listing." },
];

export default function SellLanding() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isConsumer = user && (user.account_type || "PROFESSIONAL") === "CONSUMER";

  const onList = () => {
    if (!user) { nav("/login"); return; }
    if (isConsumer) { nav("/sell/new"); return; }
    nav("/have-property"); // professionals use their existing listing flow
  };

  return (
    <div className="min-h-screen bg-background" data-testid="sell-landing-page">
      <Seo
        title="Sell Your Property — PropertyConnect"
        description="List your property with PropertyConnect. Submit your home and get represented by a vetted real-estate professional."
        canonical="/sell"
        robots="index,follow"
      />
      {user ? <Navbar /> : <PublicHeader />}
      <section className="relative overflow-hidden bg-[#0F2C59] text-white">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-20 sm:py-28">
          <p className="text-[#C79A3B] font-semibold uppercase tracking-widest text-xs mb-4">Sell with PropertyConnect</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] max-w-3xl">
            Sell your property with the right connections.
          </h1>
          <p className="text-slate-300 text-base sm:text-lg mt-6 max-w-2xl">
            Owners can submit a property for review and be matched with a trusted real estate professional who will
            represent and market the listing to serious buyers.
          </p>
          <Button data-testid="sell-list-cta" onClick={onList} className="mt-8 h-12 px-7 rounded-xl bg-[#C79A3B] hover:bg-[#C79A3B]/90 text-white text-base font-semibold">
            List My Property <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-4 sm:px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <div key={i} data-testid={`sell-step-${i}`} className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="h-12 w-12 rounded-xl bg-[#0F2C59]/5 flex items-center justify-center mb-4"><s.icon className="h-6 w-6 text-[#0F2C59]" /></div>
              <div className="text-xs font-bold text-[#C79A3B] mb-1">STEP {i + 1}</div>
              <h3 className="font-semibold text-slate-900 text-lg">{s.title}</h3>
              <p className="text-sm text-slate-500 mt-1.5">{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 text-center">
          <Button data-testid="sell-list-cta-bottom" onClick={onList} className="h-12 px-7 rounded-xl bg-[#0F2C59] hover:bg-[#0F2C59]/90 text-white text-base font-semibold">
            List My Property <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
          <p className="text-xs text-slate-400 mt-3">Submissions are reviewed before being published. You keep full visibility of your submission status.</p>
        </div>
      </section>
      <SiteFooter variant={user ? "compact" : "full"} />
    </div>
  );
}
